import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import FeaturedAdsCarousel from "@/components/FeaturedAdsCarousel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useApp } from "@/contexts/AppContext";
import { applyPosterFallback, getEventId, getEventPoster } from "@/lib/eventDisplay";
import { CalendarDays, MapPin, Music2, Search, Ticket } from "lucide-react";

const CAMPAIGN_KEY = "bollywood-nights";
const HERO_IMAGE = "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=1800&q=85";

function formatSchedule(event) {
  const date = event.date ? new Date(`${event.date}T12:00:00`) : null;
  const dateText = date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })
    : "Date TBC";
  return [dateText, event.time].filter(Boolean).join(" · ");
}

function lowestPrice(event) {
  const values = (event.prices || []).map((item) => Number(item.price)).filter(Number.isFinite);
  if (Number.isFinite(Number(event.price_from))) values.push(Number(event.price_from));
  return values.length ? Math.min(...values) : null;
}

export default function Bollywood() {
  const navigate = useNavigate();
  const { city, setCity, cities, language } = useApp();
  const [events, setEvents] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams();
    if (city) params.set("city", city);
    if (language && language !== "All") params.set("language", language);
    setLoading(true);
    api.get(`/campaigns/${CAMPAIGN_KEY}/events?${params.toString()}`)
      .then((r) => setEvents(Array.isArray(r.data) ? r.data : []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [city, language]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return events;
    return events.filter((event) => [
      event.title,
      event.description,
      event.venue,
      event.city,
      event.language,
    ].some((value) => String(value || "").toLowerCase().includes(needle)));
  }, [events, query]);

  const openEvent = (event) => {
    const eventId = getEventId(event);
    if (eventId) navigate(`/event/${eventId}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <section className="relative min-h-[360px] overflow-hidden border-b border-border">
        <img src={HERO_IMAGE} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative page-shell flex min-h-[360px] flex-col justify-end py-10 text-white">
          <div className="max-w-4xl">
            <p className="text-xs font-black uppercase tracking-[0.35em] text-primary">Bollywood Nights</p>
            <h1 className="mt-3 font-display text-4xl font-black leading-tight sm:text-5xl lg:text-6xl">
              Bollywood parties, DJ nights and cinema-led experiences
            </h1>
            <p className="mt-4 max-w-2xl text-sm text-white/80 md:text-base">
              Discover Bollywood events across the UK, with featured placements for organisers and sponsors.
            </p>
          </div>
        </div>
      </section>

      <main className="page-shell py-10">
        <section className="space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.25em] text-primary">Featured Ads</p>
              <h2 className="mt-1 font-display text-2xl font-black">Promoted Bollywood picks</h2>
            </div>
            <Button variant="outline" className="rounded-full" onClick={() => navigate("/my-events")}>
              Promote your event
            </Button>
          </div>
          <FeaturedAdsCarousel language={language} campaign={CAMPAIGN_KEY} />
        </section>

        <section className="mt-10">
          <div className="grid gap-3 rounded-lg border border-border bg-card p-3 md:grid-cols-[1fr_auto]">
            <label className="relative block">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search Bollywood events, venues or areas"
                className="h-11 w-full rounded-md border border-input bg-background pl-10 pr-3 text-sm outline-none focus:border-primary"
              />
            </label>
            <select value={city} onChange={(e) => setCity(e.target.value)} className="h-11 rounded-md border border-input bg-background px-3 text-sm font-bold outline-none focus:border-primary">
              {(cities || []).map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => <div key={index} className="h-72 rounded-lg border border-border bg-muted/40" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="mt-6 rounded-lg border border-dashed border-border p-8 text-center">
              <Music2 className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-3 font-display text-2xl font-black">No Bollywood listings found</p>
              <p className="mt-2 text-sm text-muted-foreground">Tag events with the Bollywood Nights campaign to show them here.</p>
            </div>
          ) : (
            <div className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {filtered.map((event) => <BollywoodCard key={getEventId(event) || event.title} event={event} onOpen={openEvent} />)}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function BollywoodCard({ event, onOpen }) {
  const price = lowestPrice(event);
  return (
    <article className="overflow-hidden rounded-lg border border-border bg-card transition hover:-translate-y-0.5 hover:border-primary/50">
      <button onClick={() => onOpen(event)} className="block w-full text-left">
        <div className="relative aspect-[16/10] overflow-hidden bg-muted">
          <img src={getEventPoster(event)} alt={event.title} loading="lazy" decoding="async" onError={(e) => applyPosterFallback(e, event)} className="h-full w-full object-cover transition duration-500 hover:scale-105" />
          <div className="absolute left-3 top-3 flex flex-wrap gap-2">
            <Badge className="bg-primary text-primary-foreground"><Music2 className="mr-1 h-3 w-3" /> Bollywood</Badge>
            {event.language && <Badge variant="secondary">{event.language}</Badge>}
          </div>
        </div>
        <div className="p-4">
          <h3 className="font-display text-xl font-black leading-tight line-clamp-2">{event.title}</h3>
          <div className="mt-3 space-y-2 text-sm text-muted-foreground">
            <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-primary" /> {formatSchedule(event)}</p>
            <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> {event.venue || "Venue TBC"}, {event.city || "UK"}</p>
          </div>
          <div className="mt-4 flex items-center justify-between gap-3">
            <span className="text-xs font-bold text-primary">{event.festival_campaign || "bollywood-nights"}</span>
            <span className="inline-flex items-center gap-1 text-sm font-black">
              <Ticket className="h-4 w-4" />
              {price === null ? "View tickets" : `from £${price}`}
            </span>
          </div>
        </div>
      </button>
    </article>
  );
}
